// ============================================================================
// Componente: AutomacaoPL.js
// Objetivo: Interface para automação da Análise de P&L (Outliers e Z-Score).
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import { 
    TrendingUp, Download, AlertCircle, CheckCircle2, 
    ArrowLeft, Loader2, Info, Globe, Database, 
    FileSpreadsheet, UploadCloud, HelpCircle 
} from 'lucide-react';
import HelpCenter from '../../HelpCenter';

// `embedded`: quando true, renderizada DENTRO do módulo Contabilidade (que já traz
// Sidebar + cabeçalho). Escondemos Sidebar/cabeçalho/offset próprios. Default false = página inteira.
const AutomacaoPL = ({ embedded = false }) => {
    const navigate = useNavigate();
    
    // Controle de Layout
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
    const [isHelpOpen, setIsHelpOpen] = useState(false);

    // Regras de Perfil do Usuário
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const nomeUsuario = user?.nome ? user.nome.split(' ')[0] : 'Colaborador';
    
    useEffect(() => {
        const handleResize = () => { 
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            if (mobile) setIsCollapsed(true);
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getMarginLeft = () => isMobile ? '0px' : (isCollapsed ? '80px' : '260px');

    const [arquivosPL, setArquivosPL] = useState([]);
    const [periodos, setPeriodos] = useState([]);
    const [periodoSelecionado, setPeriodoSelecionado] = useState('');
    const [idioma, setIdioma] = useState('PT');
    
    const [carregando, setCarregando] = useState(false);
    const [tipoAcao, setTipoAcao] = useState(''); 
    const [mensagem, setMensagem] = useState(null);

    const [progress, setProgress] = useState(0);
    const [statusText, setStatusText] = useState('');
    const [isPolling, setIsPolling] = useState(false);
    const [executionId, setExecutionId] = useState(null);

    const fetchPeriodos = async () => {
        try {
            const res = await fetch('/api/contabilidade/pl-periodos', {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
            });
            const data = await res.json();
            if (Array.isArray(data)) {
                setPeriodos(data);
                if (data.length > 0) setPeriodoSelecionado(data[0]);
            }
        } catch (error) { console.error("Erro ao carregar períodos."); }
    };

    useEffect(() => { fetchPeriodos(); }, []);

    const hoje = new Date();
    const mesAnterior = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
    const mesAnteriorStr = `${mesAnterior.getFullYear()}-${String(mesAnterior.getMonth() + 1).padStart(2, '0')}-01`;
    const dadosAtualizados = periodos.includes(mesAnteriorStr);

    const formatarDataMes = (dataStr) => {
        if (!dataStr) return '';
        const [ano, mes] = dataStr.split('-');
        const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
        return `${meses[parseInt(mes) - 1]}/${ano}`;
    };

    // Polling Progress
    useEffect(() => {
        let interval;
        if (isPolling && executionId) {
            interval = setInterval(async () => {
                try {
                    const res = await fetch(`/api/contabilidade/logs/${executionId}`, {
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
                    });
                    const data = await res.json();
                    if (data.logs && data.logs.length > 0) {
                        const lastLog = data.logs[data.logs.length - 1];
                        const cleanLog = lastLog.replace(/(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2},\d{3}\s*-\s*INFO\s*-\s*|\[.*?\]\s*)/g, '');
                        setStatusText(cleanLog);

                        if (tipoAcao === 'upload') {
                            if (cleanLog.includes('Lendo arquivo')) setProgress((prev) => Math.min(prev + 5, 50));
                            else if (cleanLog.includes('Banco de Dados')) setProgress(80);
                            else if (cleanLog.includes('Sucesso') || cleanLog.includes('100%')) setProgress(100);
                        } else if (tipoAcao === 'gerar') {
                            if (cleanLog.includes('Buscando dados')) setProgress(20);
                            else if (cleanLog.includes('processado')) setProgress((prev) => Math.min(prev + 10, 70));
                            else if (cleanLog.includes('Gerando relatório')) setProgress(85);
                            else if (cleanLog.includes('Sucesso')) setProgress(100);
                        } else if (tipoAcao === 'exportar') {
                            if (cleanLog.includes('Extraindo dados')) setProgress(30);
                            else if (cleanLog.includes('Transformando')) setProgress(70);
                            else if (cleanLog.includes('Sucesso')) setProgress(100);
                        }
                    }
                } catch(e) {}
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [isPolling, executionId, tipoAcao]);

    const resetState = () => {
        setMensagem(null);
        setProgress(0);
        setStatusText('');
        setExecutionId(null);
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        setCarregando(true); setTipoAcao('upload'); resetState();
        setProgress(5); setStatusText('A preparar ficheiros...');
        
        const newId = Date.now().toString();
        setExecutionId(newId); setIsPolling(true);

        const formData = new FormData();
        formData.append('executionId', newId);
        for (let i = 0; i < arquivosPL.length; i++) {
            formData.append('arquivos_pl', arquivosPL[i]);
        }

        try {
            const response = await fetch('/api/contabilidade/pl-upload', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
                body: formData
            });
            if (response.ok) {
                setProgress(100); setStatusText('Base de Dados Atualizada!');
                setMensagem({ type: 'success', text: 'Dados inseridos com sucesso no Data Warehouse!' });
                setArquivosPL([]); 
                if(document.getElementById('input-pl')) document.getElementById('input-pl').value = '';
                fetchPeriodos(); 
            } else {
                const err = await response.json();
                setMensagem({ type: 'error', text: err.error || 'Erro na importação.' });
            }
        } catch (error) {
            setMensagem({ type: 'error', text: 'Erro de conexão com o servidor.' });
        } finally {
            setCarregando(false); setTimeout(() => { setIsPolling(false); }, 3000);
        }
    };

    const handleGerarRelatorio = async () => {
        if (!periodoSelecionado) return;
        setCarregando(true); setTipoAcao('gerar'); resetState();
        setProgress(5); setStatusText('A extrair dados do Warehouse...');
        
        const newId = Date.now().toString();
        setExecutionId(newId); setIsPolling(true);

        try {
            const response = await fetch('/api/contabilidade/pl-gerar', {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ executionId: newId, periodo: periodoSelecionado, idioma: idioma })
            });

            if (response.ok) {
                const blob = await response.blob();
                let fileName = `Relatorio_PL_${idioma}.pdf`;
                const contentDisposition = response.headers.get('content-disposition');
                if (contentDisposition) {
                    const match = contentDisposition.match(/filename="?(.*?)"?(;|$)/);
                    if (match && match[1]) fileName = match[1].replace(/['"]/g, ''); 
                }

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url; link.setAttribute('download', fileName);
                document.body.appendChild(link); link.click(); link.parentNode.removeChild(link);
                
                
                setProgress(100); setStatusText('Relatório Transferido!');
                setMensagem({ type: 'success', text: 'Relatório Analítico PDF gerado com sucesso!' });
            } else {
                const err = await response.json();
                setMensagem({ type: 'error', text: err.error || 'Erro ao gerar PDF.' });
            }
        } catch (error) {
            setMensagem({ type: 'error', text: 'Erro de conexão com o servidor.' });
        } finally {
            setCarregando(false); setTimeout(() => { setIsPolling(false); }, 3000);
        }
    };

    const handleExportarExcel = async () => {
        if (!periodoSelecionado) return;
        setCarregando(true); setTipoAcao('exportar'); resetState();
        setProgress(5); setStatusText('Preparando extração de dados...');
        
        const newId = Date.now().toString();
        setExecutionId(newId); setIsPolling(true);

        try {
            const response = await fetch('/api/contabilidade/pl-exportar', {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ executionId: newId, periodo: periodoSelecionado })
            });

            if (response.ok) {
                const blob = await response.blob();
                let fileName = `Exportacao_PL_${periodoSelecionado}.xlsx`;
                const contentDisposition = response.headers.get('content-disposition');
                if (contentDisposition) {
                    const match = contentDisposition.match(/filename="?(.*?)"?(;|$)/);
                    if (match && match[1]) fileName = match[1].replace(/['"]/g, ''); 
                }

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url; link.setAttribute('download', fileName);
                document.body.appendChild(link); link.click(); link.parentNode.removeChild(link);
                
                
                setProgress(100); setStatusText('Excel Transferido!');
                setMensagem({ type: 'success', text: 'Base de dados extraída com sucesso!' });
            } else {
                const err = await response.json();
                setMensagem({ type: 'error', text: err.error || 'Erro ao extrair dados.' });
            }
        } catch (error) {
            setMensagem({ type: 'error', text: 'Erro de conexão com o servidor.' });
        } finally {
            setCarregando(false); setTimeout(() => { setIsPolling(false); }, 3000);
        }
    };

    const corModulo = '#16a34a'; // Verde
    
    const getProgressBarColor = () => {
        if (mensagem?.type === 'error') return '#ef4444'; // Vermelho
        if (mensagem?.type === 'success') return '#10b981'; // Verde
        return corModulo; // Verde Padrão (Processando)
    };

    return (
        <div style={embedded ? { width: '100%' } : { display: 'flex', minHeight: '100vh', backgroundColor: '#f4f6f8' }}>
            {!embedded && <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />}
            
            <div style={{ flex: 1, marginLeft: embedded ? 0 : getMarginLeft(), transition: 'margin-left 0.3s ease', width: '100%' }}>
                
                {/* CABEÇALHO PREMIUM */}
                {!embedded && (
                <div style={{
                    height: '85px', backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', display: 'flex', 
                    alignItems: 'center', justifyContent: 'space-between', 
                    padding: isMobile ? '0 20px 0 60px' : '0 40px', position: 'sticky', top: 0, zIndex: 20 
                }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <button onClick={() => navigate('/automacoes')} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
                                <ArrowLeft size={14} /> Voltar
                            </button>
                            <span style={{ margin: '0 4px' }}>/</span> Automação & Processos <span style={{ margin: '0 4px' }}>/</span> Contábil
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <TrendingUp size={24} color={corModulo} /> Análise de P&L
                            </h2>
                            <button 
                                type="button"
                                onClick={() => setIsHelpOpen(true)}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 12px', backgroundColor: '#f0fdf4', color: corModulo, border: '1px solid #dcfce7', borderRadius: '99px', fontWeight: '600', cursor: 'pointer', fontSize: '12px', transition: 'all 0.2s' }}
                            >
                                <HelpCircle size={14} /> Como Utilizar
                            </button>
                        </div>
                    </div>
                    
                    {!isMobile && (
                        <span style={{ fontSize: '15px', fontWeight: '600', color: '#475569' }}>
                            Olá, {nomeUsuario} 👋
                        </span>
                    )}
                </div>
                )}

                <div style={{ padding: isMobile ? '20px' : '40px' }} className="max-w-6xl mx-auto">
                    
                    {/* INFO BOXES */}
                    <div style={{ backgroundColor: '#f0fdf4', borderLeft: `4px solid ${corModulo}`, padding: '16px 20px', borderRadius: '8px', marginBottom: '24px' }}>
                        <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#166534', fontSize: '15px', fontWeight: '700', marginBottom: '8px', margin: 0 }}>
                            <Info size={18} /> Porque utilizar este relatório?
                        </h4>
                        <p style={{ color: '#14532d', fontSize: '14px', lineHeight: '1.6', margin: 0, marginTop: '4px' }}>
                            Este módulo de Inteligência de Negócios não apenas soma valores, ele <strong>identifica anomalias (Outliers)</strong>. Utilizamos a metodologia <strong>Z-Score</strong> para encontrar despesas que fogem do padrão matemático da empresa.
                        </p>
                    </div>

                    {!dadosAtualizados && periodos.length > 0 && (
                        <div style={{ padding: '16px 20px', borderRadius: '8px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px', backgroundColor: '#fef2f2', color: '#b91c1c', border: `1px solid #fecaca`}}>
                            <AlertCircle size={24} />
                            <span style={{ fontSize: '14px', lineHeight: '1.5' }}>
                                <strong>Aviso Importante:</strong> Ainda não constam na base os dados do mês anterior ({formatarDataMes(mesAnteriorStr)}). Por favor, alimente a base de dados antes de gerar relatórios recentes para garantir a precisão da análise.
                            </span>
                        </div>
                    )}

                    {/* MAIN CARDS GRID */}
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
                        
                        {/* CARD 1: UPLOAD WAREHOUSE */}
                        <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '30px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                                <Database size={20} color={corModulo} /> 1. Alimentar Data Warehouse
                            </h3>
                            <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '20px', flex: 1 }}>Faça o upload dos arquivos .csv para atualizar a base histórica necessária para o cálculo do Z-Score.</p>
                            
                            <form onSubmit={handleUpload}>
                                <div style={{ 
                                    border: '2px dashed #cbd5e1', borderRadius: '12px', padding: '30px 20px', 
                                    textAlign: 'center', backgroundColor: '#f8fafc', cursor: 'pointer',
                                    transition: 'all 0.2s ease', position: 'relative', marginBottom: '20px'
                                }}>
                                    <input 
                                        id="input-pl" type="file" multiple accept=".csv"
                                        onChange={(e) => { setArquivosPL(e.target.files); setMensagem(null); }} 
                                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} 
                                        required disabled={carregando}
                                    />
                                    
                                    {arquivosPL.length === 0 ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                            <UploadCloud size={32} color="#94a3b8" />
                                            <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#475569' }}>Selecionar Ficheiros CSV</p>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                            <FileSpreadsheet size={32} color={corModulo} />
                                            <p style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: corModulo }}>{arquivosPL.length} arquivo(s) selecionado(s)</p>
                                        </div>
                                    )}
                                </div>
                                <button type="submit" disabled={carregando || arquivosPL.length === 0} style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px', borderRadius: '8px', backgroundColor: carregando || arquivosPL.length === 0 ? '#cbd5e1' : corModulo, color: '#fff', fontWeight: '600', border: 'none', cursor: carregando || arquivosPL.length === 0 ? 'not-allowed' : 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(22, 163, 74, 0.2)' }}>
                                    {carregando && tipoAcao === 'upload' ? <><Loader2 size={18} className="animate-spin" /> Processando...</> : <><Database size={18} /> Enviar para Base de Dados</>}
                                </button>
                            </form>
                        </div>

                        {/* CARD 2: EXTRAÇÃO E RELATÓRIOS */}
                        <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '30px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                                <TrendingUp size={20} color={corModulo} /> 2. Extração e Relatórios
                            </h3>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', flex: 1 }}>
                                
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>Selecionar Período Alvo:</label>
                                    <select 
                                        value={periodoSelecionado} 
                                        onChange={(e) => setPeriodoSelecionado(e.target.value)}
                                        style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', backgroundColor: '#f8fafc', color: '#334155', fontWeight: '500' }}
                                    >
                                        {periodos.length === 0 ? <option value="">Sem dados na base</option> : periodos.map(p => (
                                            <option key={p} value={p}>{formatarDataMes(p)}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>Idioma do Relatório (PDF):</label>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <button onClick={() => setIdioma('PT')} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: `1px solid ${idioma === 'PT' ? corModulo : '#cbd5e1'}`, backgroundColor: idioma === 'PT' ? '#f0fdf4' : '#fff', color: idioma === 'PT' ? corModulo : '#64748b', fontWeight: '600', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', transition: 'all 0.2s' }}>
                                            <Globe size={16}/> Português
                                        </button>
                                        <button onClick={() => setIdioma('EN')} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: `1px solid ${idioma === 'EN' ? corModulo : '#cbd5e1'}`, backgroundColor: idioma === 'EN' ? '#f0fdf4' : '#fff', color: idioma === 'EN' ? corModulo : '#64748b', fontWeight: '600', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', transition: 'all 0.2s' }}>
                                            <Globe size={16}/> English
                                        </button>
                                    </div>
                                </div>

                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '20px' }}>
                                <button onClick={handleGerarRelatorio} disabled={carregando || periodos.length === 0} style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px', borderRadius: '8px', backgroundColor: carregando || periodos.length === 0 ? '#cbd5e1' : corModulo, color: '#fff', fontWeight: '600', border: 'none', cursor: carregando || periodos.length === 0 ? 'not-allowed' : 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(22, 163, 74, 0.2)' }}>
                                    {carregando && tipoAcao === 'gerar' ? <><Loader2 size={18} className="animate-spin" /> Analisando...</> : <><Download size={18} /> Exportar PDF Analítico</>}
                                </button>
                                
                                <button onClick={handleExportarExcel} disabled={carregando || periodos.length === 0} style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px', borderRadius: '8px', border: `2px solid ${carregando || periodos.length === 0 ? '#cbd5e1' : corModulo}`, backgroundColor: '#fff', color: carregando || periodos.length === 0 ? '#94a3b8' : corModulo, fontWeight: '600', cursor: carregando || periodos.length === 0 ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}>
                                    {carregando && tipoAcao === 'exportar' ? <><Loader2 size={18} className="animate-spin" /> Extraindo...</> : <><FileSpreadsheet size={18} /> Extrair Base (Excel)</>}
                                </button>
                            </div>
                        </div>

                    </div>

                    {/* ALERTAS */}
                    {mensagem && !carregando && (
                        <div style={{ backgroundColor: mensagem.type === 'success' ? '#ecfdf5' : '#fef2f2', border: `1px solid ${mensagem.type === 'success' ? '#a7f3d0' : '#fecaca'}`, color: mensagem.type === 'success' ? '#059669' : '#ef4444', padding: '16px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', fontSize: '14px', fontWeight: '500' }}>
                            {mensagem.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />} 
                            {mensagem.text}
                        </div>
                    )}

                    {/* CARD DE PROGRESSO */}
                    {(carregando || progress > 0) && (
                        <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    {carregando && <Loader2 size={24} color="#0ea5e9" className="animate-spin" />}
                                    {!carregando && mensagem?.type === 'success' && <CheckCircle2 size={24} color="#10b981" />}
                                    {!carregando && mensagem?.type === 'error' && <AlertCircle size={24} color="#ef4444" />}
                                    
                                    <div>
                                        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>Status da Execução</h4>
                                        <p style={{ margin: 0, fontSize: '14px', color: '#64748b', marginTop: '2px' }}>{statusText}</p>
                                    </div>
                                </div>
                                <span style={{ fontSize: '20px', fontWeight: '800', color: getProgressBarColor() }}>
                                    {progress}%
                                </span>
                            </div>

                            <div style={{ width: '100%', height: '12px', backgroundColor: '#f1f5f9', borderRadius: '99px', overflow: 'hidden' }}>
                                <div style={{ 
                                    width: `${progress}%`, 
                                    height: '100%', 
                                    backgroundColor: getProgressBarColor(),
                                    borderRadius: '99px',
                                    transition: 'width 0.5s ease-in-out, background-color 0.3s ease'
                                }} />
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {/* CHAMADA DO MANUAL COM FILTRO EXCLUSIVO PARA ANÁLISE P&L */}
            <HelpCenter 
                isOpen={isHelpOpen} 
                onClose={() => setIsHelpOpen(false)} 
                secaoInicial="pl-zscore" 
                showOnlyIds={['pl-zscore']} 
            />
        </div>
    );
};

export default AutomacaoPL;